import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import NoAccess from "./NoAccess";
import { AccessError, GetEntityTypes, SubmitBackfillEntity } from "../services/ApiService";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Select,
  Space,
  Typography,
} from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const { Title, Text } = Typography;

export default function BackfillEntity() {
  const auth = useAuth();
  const token = auth.user?.id_token;

  const [entityTypes, setEntityTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [accessError, setAccessError] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!token) return;
    GetEntityTypes(token)
      .then((data) => setEntityTypes(data.entity_types || []))
      .catch((e) => {
        if (e instanceof AccessError) setAccessError(true);
        else setError(e.message);
      });
  }, [token]);

  if (accessError) return <NoAccess />;

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setResult(null);
    setError(null);
    try {
      const payload = {
        application_id: values.application_id.trim(),
        entity_type: values.entity_type,
        entity_value: values.entity_value.trim(),
        start_measurement_date: dayjs(values.start_measurement_date).utc().toISOString(),
        end_measurement_date: values.end_measurement_date
          ? dayjs(values.end_measurement_date).utc().toISOString()
          : "",
      };
      const data = await SubmitBackfillEntity(token, payload);
      setResult(data.s3_path);
      form.resetFields();
    } catch (e) {
      if (e instanceof AccessError) setAccessError(true);
      else setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: 640 }}>
      <Title level={3}>Manual Backfill Entity</Title>
      <Text type="secondary">
        Register an entity for backfill. The record will be written to S3 and
        picked up by the next consolidation run.
      </Text>

      <Card style={{ marginTop: 24 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            label="Application ID"
            name="application_id"
            rules={[{ required: true, message: "Application ID is required" }]}
          >
            <Input placeholder="e.g. SA-6087" />
          </Form.Item>

          <Form.Item
            label="Entity Type"
            name="entity_type"
            rules={[{ required: true, message: "Entity type is required" }]}
          >
            <Select
              placeholder="Select entity type"
              options={entityTypes.map((t) => ({ label: t, value: t }))}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Entity Value (ID or Name)"
            name="entity_value"
            rules={[{ required: true, message: "Entity value is required" }]}
          >
            <Input placeholder="e.g. abc123xyz" />
          </Form.Item>

          <Form.Item
            label="Start Measurement Date"
            name="start_measurement_date"
            rules={[{ required: true, message: "Start date is required" }]}
          >
            <DatePicker
              showTime
              style={{ width: "100%" }}
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Select start date and time"
            />
          </Form.Item>

          <Form.Item
            label="End Measurement Date"
            name="end_measurement_date"
            dependencies={["start_measurement_date"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const start = getFieldValue("start_measurement_date");
                  if (!value || !start || !value.isBefore(start)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("End date must be on or after the start date")
                  );
                },
              }),
            ]}
          >
            <DatePicker
              showTime
              style={{ width: "100%" }}
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Select end date and time (optional)"
            />
          </Form.Item>

          {error && (
            <Form.Item>
              <Alert type="error" message={error} showIcon />
            </Form.Item>
          )}

          {result && (
            <Form.Item>
              <Alert
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                message="Entity submitted successfully"
                description={
                  <Space direction="vertical" size={2}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Written to:
                    </Text>
                    <Text code style={{ fontSize: 11 }}>
                      {result}
                    </Text>
                  </Space>
                }
              />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
